import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { connect, useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from '@mui/icons-material/Add';
import { saveUserProfile } from "../../../redux/slices/updateUserSlice";
import Checkbox from "../../shared/Input/Checkbox/Checkbox";
import InputController from "../../shared/Input/InputField/InputController";
import Button from "../../shared/Button/Button";
import RowRadioButtonsGroup from "../../shared/Input/RadioButton/RadioButton";
import { uiDateFormat } from "../../../lib/utils/dateAndTimeFormatting";
import { GENDER } from "../../../lib/constants/globalConstant";
import { myProfileValidationSchema } from "../updateValidationSchema";
import { myProfileStyles as styles } from "../myContact.tailwind";
import UpdateEmailAndPhoneModal from "../../shared/Modals/UpdateEmailAndPhoneModal/UpdateEmailAndPhoneModal";
import OptInForUpdateEmailModal from "../../shared/Modals/OptInForUpdatesModal/OptInForUpdateEmailModal";
import DatePickers from "../../shared/Input/DatePicker/DatePicker";

const genderOptions = [
  { value: GENDER.MALE, label: "Male" },
  { value: GENDER.FEMALE, label: "Female" },
  { value: GENDER.OTHER, label: "Other" },
];
const booleanToNumeric = (value) => (value ? 1 : 0); // converts the boolean values to numeric

const MyProfile = ({ saveProfile }) => {
  const { user } = useSelector((state) => state.user);
  const { updatingUser } = useSelector((state) => state.updateUser);
  const profileDetails = user?.userDetails; // Details present in userDetails

  const [isOpen, setIsOpen] = useState(false); //state to open modal
  const [isUpdateEmailModalOpen, setIsUpdateEmailModalOpen] = useState(false);
  const [fieldToUpdate, setFieldToUpdate] = useState(""); // to keep track of what value needs to be updated through modal (email or phoneNumer)
  const [isFormDirty, setIsFormDirty] = useState(false); // Custom "dirty" state

  const initialValues = {
    userEmail: profileDetails?.userEmail || "",
    userNumber: profileDetails?.userCellNumber || "",
    userFirstName: profileDetails?.userFirstName || "",
    userLastName: profileDetails?.userLastName || "",
    userNickName: profileDetails?.userNickName || "",
    dob: profileDetails?.dob ? uiDateFormat(profileDetails?.dob) : "",
    gender: profileDetails?.gender || "",
    emailAlertPreference: booleanToNumeric(profileDetails?.emailAlertPreference || false),
    textAlertPreference: booleanToNumeric(profileDetails?.textAlertPreference || false),
    optInForUpdateEmail: profileDetails?.optInForUpdateEmail || "",
  };

  // React Hook Form
  const { control, handleSubmit, setValue, watch, getValues, setError, formState: { errors, isLoading } } = useForm({
    resolver: yupResolver(myProfileValidationSchema),
    defaultValues: initialValues
  });

  // Watch for changes in form fields
  const watchedValues = watch();

  const onSubmit = async(data) => {
    let hasError = false;

    if(!data?.optInForUpdateEmail && data?.emailAlertPreference) {
      setError("optInForUpdateEmail", {
        type: "manual",
        message: "Please add an email to receive updates.",
      });
      hasError = true;  
    }

    if(!data?.userNumber && data?.textAlertPreference) {
      setError("userNumber", {
        type: "manual",
        message: "Please add a phone number to receive updates.",
      });
      hasError = true;
    }

    if(hasError) {
      return;
    }
    
    const profileData = {
      userFirstName: data?.userFirstName,
      userLastName: data?.userLastName,
      userNickName: data?.userNickName,
      dob: data?.dob,
      gender: parseInt(data?.gender),
      emailAlertPreference: data?.emailAlertPreference,
      textAlertPreference: data?.textAlertPreference,
    };
    saveProfile(profileData);
  };

  const updateFieldFromModal = (field, fieldToUpdate) => {
    setValue(field, fieldToUpdate);
  }

  const handleOpenModal = (field) => {
    setFieldToUpdate(field);
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setFieldToUpdate("");
  };

  const handleOptInForUpdateEmail = () => {
    setIsUpdateEmailModalOpen(true);
  }

  const handleOptInForUpdateEmailClose = () => {
    setIsUpdateEmailModalOpen(false);
  }

  const checkIsFormDirty = () => {
    const currentValues = getValues();
    const initialValues = {
      userEmail: profileDetails?.userEmail || "",
      userNumber: profileDetails?.userCellNumber || "",
      userFirstName: profileDetails?.userFirstName || "",
      userLastName: profileDetails?.userLastName || "",
      userNickName: profileDetails?.userNickName || "",
      dob: profileDetails?.dob ? uiDateFormat(profileDetails?.dob) : "",
      gender: profileDetails?.gender || "",
      emailAlertPreference: booleanToNumeric(profileDetails?.emailAlertPreference || false),
      textAlertPreference: booleanToNumeric(profileDetails?.textAlertPreference || false),
      optInForUpdateEmail: profileDetails?.optInForUpdateEmail || ""
    };

    // Compare the current values with the initial values
    const isDirty = Object.keys(currentValues).some(
      (key) => currentValues[key] !== initialValues[key]
    );
    setIsFormDirty(isDirty); // Update custom "dirty" state
  };

   // Re-run the custom dirty check whenever the form values change
  useEffect(() => {
    checkIsFormDirty();
  }, [watchedValues]);

   // Set error for 'emailAlertPreference' and 'optInForUpdateEmail' on change
   useEffect(() => {
    if (watchedValues?.textAlertPreference && !watchedValues?.userNumber) {
      setError("userNumber", {
        type: "manual",
        message: "Please add a phone number to receive updates.", 
      });
    }else{
      setError("userNumber", {
        type: "manual",
        message: "",
      });
    }

    if (watchedValues?.emailAlertPreference && !watchedValues?.optInForUpdateEmail) {
      setError("optInForUpdateEmail", {
        type: "manual",
        message: "Please add an email to receive updates.",
      });
    } else {
      setError("optInForUpdateEmail", {
        type: "manual",
        message: "",
      });
    }
  }, [watchedValues?.emailAlertPreference, watchedValues?.optInForUpdateEmail, watchedValues?.textAlertPreference, watchedValues?.userNumber, setError]);


  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
        <div className={styles.inputContainer}>
          <div className={styles.editContainer}>
            <button type="button" className={styles.button} onClick={() => handleOpenModal("userEmail")}>
              {/* Button to open modal  displays the change Email if there is email present or Add Email if email is not present*/}
              {profileDetails?.userEmail ? (
                <>
                  <EditIcon sx={{ fontSize: "12px" }} />
                  Change Email
                </>
              ) : (
                <>
                  <AddIcon sx={{ fontSize: "12px" }} />
                  Add Email
                </>
              )}
            </button>

            {/* Email */}
            <InputController
              showLabel={true}
              name="userEmail"
              control={control}
              label="Email"
              disabled={true}
            />
          </div>

          <div className={styles.editContainer}>
            <button type="button" className={styles.button} onClick={() => handleOpenModal("userNumber")}>

              {/* Button to open modal  displays the change Email if there is email present or Add Email if email is not present*/}
              {profileDetails?.userCellNumber ? (
                <>
                  <EditIcon sx={{ fontSize: "12px" }} />
                  Change Phone Number
                </>
              ) : (
                <>
                  <AddIcon sx={{ fontSize: "12px" }} />
                  <span>Add Phone Number</span>
                </>
              )}
            </button>

            {/* Phone Number */}
            <InputController
              showLabel={true}
              name="userNumber"
              control={control}
              label="Phone Number"
              disabled={true}
              error={!!errors.userNumber}
              helperText={errors.userNumber?.message}
            />
          </div>
        </div>

        {/* First Name */}
        <div className={styles.inputContainer}>
          <InputController
            showLabel={true}
            name="userFirstName"
            control={control}
            label="First Name"
            className={styles.input}
            error={!!errors.userFirstName}
            helperText={errors.userFirstName?.message}
          />

          {/* Last Name */}
          <InputController
            showLabel={true}
            name="userLastName"
            control={control}
            label="Last Name"
            error={!!errors.userLastName}
            helperText={errors.userLastName?.message}
            sx={{ width: "100%" }}
          />
        </div>

        {/* Nick Name */}
        <div className={styles.inputContainer}>
          <InputController
            showLabel={true}
            name="userNickName"
            control={control}
            label="Nick Name"
            error={!!errors.userNickName}
            helperText={errors.userNickName?.message}
            required={true}
          />

          {/* Date of Birth */}
          <DatePickers
            showLabel={true}
            name="dob"
            control={control}
            label="Date of Birth"
            error={!!errors.dob}
            helperText={errors.dob?.message}
            bgColor="white"
            focusColor="#3b82f680"
            value={watch("dob")}
            onChange={(value) => setValue("dob", value)}
            disableFuture={true}
          />
        </div>

        <div className={styles.radioAndInputContainer}>
          <div className={`sm:max-w-[48%] ${styles.editContainer}`}>
              <button type="button" className={styles.button} onClick={handleOptInForUpdateEmail}>
                {/* Button to open modal  displays the change Email if there is email present or Add Email if email is not present*/}
                {profileDetails?.optInForUpdateEmail ? (
                  <>
                    <EditIcon sx={{ fontSize: "12px" }} />
                    Change Email
                  </>
                ) : (
                  <>
                    <AddIcon sx={{ fontSize: "12px" }} />
                    Add Email
                  </>
                )}
              </button>

              {/* optInForUpdateEmail */}
              <InputController
                showLabel={true}
                name="optInForUpdateEmail"
                control={control}
                label="Email For Updates"
                disabled={true}
                error={!!errors.optInForUpdateEmail}
                helperText={errors.optInForUpdateEmail?.message}
              />
          </div>

          {/* Gender */}
          <RowRadioButtonsGroup
            name="gender"
            label="Gender"
            control={control}
            defaultValue={profileDetails?.gender}
            options={genderOptions}
            error={!!errors.gender}
            helperText={errors.gender?.message}
          />
        </div>
        {/* Email Alert Preference */}
        <div className={styles.checkboxContainer}>

          {/* Email Alert Preference */}
          <Checkbox
            name="emailAlertPreference"
            control={control}
            label="Opt-in for email updates by checking this box."
            defaultValue={profileDetails?.emailAlertPreference}
          />

          {/* Text Alert Preference */}
          <Checkbox
            name="textAlertPreference"
            control={control}
            label="Opt-in for text updates by checking this box."
            defaultValue={profileDetails?.textAlertPreference}
          />

        </div>

        <div className={styles.buttonContainer}>
          <Button variant="primary" type="submit" disabled={updatingUser || !isFormDirty || isLoading} className={styles.saveButton}>
            {updatingUser ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
      {isOpen && <UpdateEmailAndPhoneModal isOpen={isOpen} onClose={handleCloseModal} updateFieldFromModal={updateFieldFromModal} fieldToUpdate={fieldToUpdate} />}
      {isUpdateEmailModalOpen && <OptInForUpdateEmailModal isOpen={isUpdateEmailModalOpen} onClose={handleOptInForUpdateEmailClose} updateFieldFromModal={updateFieldFromModal} />}
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  saveProfile: (data) => dispatch(saveUserProfile(data)),
});

export default connect(null, mapDispatchToProps)(MyProfile);
